import { useEffect, useRef, type FocusEvent, type MouseEvent } from 'react';

import { ConfigBaseItem } from '../shared/ConfigBaseItem';
import { LinkUrlIcon } from '../shared/Icons/InfoIcons';
import { ConfigInput } from '../shared/Input/ConfigInput.style';

type Props = {
  item: any;
  send?: any;
};

export function getLinkHref(value?: string | null) {
  if (value == null) return '';
  const trimmed = String(value).trim();
  if (!trimmed) return '';
  if (/^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(trimmed)) return trimmed;
  if (trimmed.startsWith('//')) return `https:${trimmed}`;
  return `https://${trimmed}`;
}

export function openLinkHref(value?: string | null) {
  const href = getLinkHref(value);
  if (!href) return;
  window.open(href, '_blank', 'noopener,noreferrer');
}

export default function Link({ item, send }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const isReadOnly = Boolean(item.FIELDREADONLY || item.PREVIEWMODE);
  const hasLink = Boolean(getLinkHref(item.FIELDVALUE));

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.value = item.FIELDVALUE || '';
    }
  }, [item.FIELDVALUE]);

  function handleBlur(e: FocusEvent<HTMLInputElement>) {
    if (!send || isReadOnly) return;
    const value = e.target.value;
    send({
      type: 'UPDATE_VALUE',
      id: item.FIELDID,
      DISPLAYVALUE: value,
      FIELDVALUE: value,
    });
  }

  function handleOpen(e: MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    openLinkHref(inputRef.current?.value ?? item.FIELDVALUE);
  }

  function handleInputClick(e: MouseEvent<HTMLInputElement>) {
    if (isReadOnly || e.metaKey || e.ctrlKey) {
      handleOpen(e);
    }
  }

  return (
    <ConfigBaseItem item={item} leftIcon={<LinkUrlIcon onClick={handleOpen} />}>
      {({ baseStatus }) => (
        <ConfigInput
          data-cy={item.FIELDID}
          ref={inputRef}
          type='text'
          readOnly={isReadOnly}
          onBlur={handleBlur}
          onClick={handleInputClick}
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          modifiers={baseStatus.status}
          style={{
            color: hasLink ? 'blue' : undefined,
            textDecoration: hasLink ? 'underline' : undefined,
            cursor: isReadOnly && hasLink ? 'pointer' : 'text',
          }}
        />
      )}
    </ConfigBaseItem>
  );
}
